/**
 * What the valuation discipline can change, and nothing else.
 *
 * The second half of the Settings page that held both. Everything here is read only by
 * the valuation: changing one moves the enterprise value and the share price, and none
 * of them changes a single figure in the accounts. That is why the two halves are now
 * two pages rather than two headings on one - an accounting user has no use for a
 * terminal growth rate, and a valuation user should not have to scroll past a
 * depreciation period to reach a discount rate.
 *
 * The method inputs come with it. The eight methods that need figures the accounts
 * cannot contain - a property valuation, a policy book, a pipeline's peak sales - had
 * nowhere to be entered on a MODEL: they were reachable only on a standalone valuation
 * project, so a model whose recommended anchor is NAV or the venture capital method had
 * a method it could not run and no way to fix that.
 *
 * `ValuationMethodInputs` took a whole `ValuationProject`, which is what made it
 * unreachable here. It now takes the saved bag and the method rows, and a model's
 * `computed.methods.results` already carries the same six fields a project's
 * `suppliedMethods` does.
 */

import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type {
  BusinessType,
  ValuationConfig,
  ValuationMethod,
} from '../../../types/financial-model';
import {
  Field,
  NumberField,
  OptionalNumberField,
  inputClass,
} from '../settingsFields';
import ValuationMethodInputs, {
  type SuppliedMethod,
} from './ValuationMethodInputs';

export function ValuationSettingsV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateSetting = useFinancialModelV2Store((s) => s.updateSetting);

  const settings = model?.settings;
  if (!model || !settings) return null;

  const valuation = settings.valuationConfig;
  const updateValuation = (patch: Partial<ValuationConfig>) =>
    updateSetting('valuationConfig', { ...valuation, ...patch });

  // A model computes every method and reports what each one needs, so the rows here
  // already exist - the forms give the eight that need supplied figures somewhere to
  // get them. Without this the method inputs were reachable only on a standalone
  // valuation project, and a model whose anchor is NAV or the VC method had a
  // recommended method it could not run.
  const methodResults = ((model.computed?.methods?.results ?? []) as unknown) as
    SuppliedMethod[];

  return (
    <div className="h-full overflow-auto p-6">
      <h2 className="mb-4 text-lg font-bold text-gray-800">Valuation settings</h2>

      <p className="mb-3 max-w-4xl text-xs text-gray-500">
        These are read only by the valuation, which works from the finished
        statements. Changing one moves the enterprise value and the share
        price; none of them changes a single figure in the accounts.
      </p>

      <div className="grid max-w-4xl grid-cols-1 gap-6">
        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">
            Valuation assumptions
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {/* First, because it decides which of the others matter. A bank's
                anchors are price/book and residual income, and a discount rate
                set with great care is close to beside the point for one. */}
            <Field
              label="What the business is"
              hint="Chooses which valuation methods are its anchors, and which need data the accounts do not carry."
            >
              <select
                value={valuation.businessType ?? 'general'}
                onChange={(e) =>
                  updateValuation({
                    businessType: e.target.value as BusinessType,
                  })
                }
                className={inputClass}
              >
                <option value="general">General trading company</option>
                <option value="bank">Bank</option>
                <option value="insurer">Insurer</option>
                <option value="manufacturer">Manufacturer</option>
                <option value="saas">SaaS</option>
                <option value="reit">REIT / property</option>
                <option value="mining">Mining</option>
                <option value="biotech">Biotech</option>
                <option value="asset_manager">Asset manager</option>
                <option value="conglomerate">Conglomerate</option>
                <option value="distressed">Distressed</option>
                <option value="utility">Utility (regulated)</option>
                <option value="startup">Startup / pre-profit</option>
                <option value="high_growth_tech">High-growth tech</option>
                <option value="commodity_holder">Commodity holder (bullion, trust)</option>
              </select>
            </Field>

            <Field label="Method">
              <select
                value={valuation.method}
                onChange={(e) =>
                  updateValuation({ method: e.target.value as ValuationMethod })
                }
                className={inputClass}
              >
                <option value="dcf">DCF</option>
                <option value="multiples">Multiples</option>
                <option value="both">Both (blended)</option>
              </select>
            </Field>
            <Field
              label="Discount rate from"
              hint="Built up: the 10-year gilt, the industry's beta relevered at the company's debt, the equity risk premium, a size premium and its own cost of debt - each shown on the Overview. The rate below is used where a part cannot be built."
            >
              <select
                value={valuation.discountRateMode ?? 'fixed'}
                onChange={(e) =>
                  updateValuation({ discountRateMode: e.target.value as 'fixed' | 'build_up' })
                }
                className={inputClass}
              >
                <option value="fixed">The rate entered</option>
                <option value="build_up">Built up from market inputs</option>
              </select>
            </Field>
            <Field
              label="Residual income after the forecast"
              hint="Growing for ever at the terminal rate, or decaying at 0.62 a year as abnormal earnings do (Dechow, Hutton and Sloan, 1999)."
            >
              <select
                value={valuation.residualIncomeForm ?? 'gordon'}
                onChange={(e) =>
                  updateValuation({ residualIncomeForm: e.target.value as 'gordon' | 'persistence' })
                }
                className={inputClass}
              >
                <option value="gordon">Grows at the terminal rate</option>
                <option value="persistence">Decays (persistence)</option>
              </select>
            </Field>
            <NumberField
              label="Discount rate"
              value={valuation.discountRate}
              onChange={(v) => updateValuation({ discountRate: v })}
              step={0.005}
              suffix={`= ${(valuation.discountRate * 100).toFixed(1)}%`}
            />
            <NumberField
              label="Terminal growth"
              value={valuation.terminalGrowthRate}
              onChange={(v) => updateValuation({ terminalGrowthRate: v })}
              step={0.005}
              suffix={`= ${(valuation.terminalGrowthRate * 100).toFixed(1)}%`}
            />
            <NumberField
              label="Exit multiple (EV/EBITDA)"
              value={valuation.exitMultipleEVEBITDA}
              onChange={(v) => updateValuation({ exitMultipleEVEBITDA: v })}
              step={0.5}
            />
            <NumberField
              label="Shares outstanding"
              value={valuation.sharesOutstanding}
              onChange={(v) => updateValuation({ sharesOutstanding: v })}
            />
            <NumberField
              label="Terminal year"
              value={valuation.terminalYear}
              onChange={(v) => updateValuation({ terminalYear: v })}
            />
          </div>

          {/* Cross-checks, not inputs. They do not move the enterprise value:
              the engine computes an implied EV from each and the Overview
              shows it beside the valuation, so a number that disagrees is
              visible rather than averaged in. Leave one blank to omit it. */}
          <h4 className="mt-5 mb-1 text-xs font-semibold text-gray-700">
            Comparable multiples
          </h4>
          <p className="mb-3 text-xs text-gray-500">
            Optional. Each one implies its own enterprise value, shown on the
            Overview as a cross-check against the valuation — they do not
            change it.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <OptionalNumberField
              label="EV / Revenue"
              value={valuation.compEVRevenue}
              onChange={(v) => updateValuation({ compEVRevenue: v })}
              step={0.1}
            />
            <OptionalNumberField
              label="EV / EBITDA"
              value={valuation.compEVEBITDA}
              onChange={(v) => updateValuation({ compEVEBITDA: v })}
            />
            <OptionalNumberField
              label="P / E"
              value={valuation.compPE}
              onChange={(v) => updateValuation({ compPE: v })}
            />
          </div>
        </section>
      </div>
      <ValuationMethodInputs
        methodInputs={
          (valuation as { methodInputs?: Record<string, unknown> | null })
            .methodInputs ?? null
        }
        results={methodResults}
        isSaving={false}
        onChange={(methodInputs) =>
          updateValuation({
            methodInputs,
          } as Partial<ValuationConfig>)
        }
      />
    </div>
  );
}
